import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { TextCTA } from "@/components/CTA";
import { useLang } from "@/i18n";
import { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Section 5 — The Layer-by-Layer Rehearsal Method.
 * Landing-page summary only: 4 steps, no technical diagrams or the full
 * nine-area methodology (those live on the dedicated methodology page,
 * linked below).
 */
export const Methodology = () => {
  const { t, l } = useLang();
  const steps = t("landing.method_steps");
  // Mobile-only accordion, one step open at a time. All four titles and
  // summaries stay visible even when collapsed — only the longer body
  // copy is hidden until expanded.
  const [openStep, setOpenStep] = useState(null);
  return (
    <section id="method" className="relative z-10 py-24 md:py-36 overflow-hidden" data-testid="methodology-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 md:mb-20">
          <Reveal className="lg:col-span-8">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("landing.method_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              {t("landing.method_h")}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4">
            <p className="hidden md:block font-body text-ink-soft leading-relaxed" data-testid="method-body-desktop">
              {t("landing.method_body")}
            </p>
            <p className="md:hidden font-body text-ink-soft leading-relaxed" data-testid="method-body-mobile">
              {t("landing.method_body_mobile")}
            </p>
          </Reveal>
        </div>

        <StaggerGroup className="border-t border-ink/20" stagger={0.05}>
          {Array.isArray(steps) &&
            steps.map((s, i) => {
              const isOpen = openStep === i;
              const toggle = () => setOpenStep(isOpen ? null : i);
              return (
                <StaggerItem key={i}>
                  <article className="group border-b border-ink/20 transition-colors duration-500 hover:bg-paper-deep/50">
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onClick={toggle}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggle();
                        }
                      }}
                      className="grid md:grid-cols-12 gap-4 md:gap-8 items-start py-7 md:py-9 cursor-pointer md:cursor-default outline-none"
                      data-testid={`method-step-trigger-${i}`}
                    >
                      <div className="md:col-span-2 flex items-center justify-between md:block">
                        <span className="font-serif text-5xl md:text-7xl text-ink/15 group-hover:text-terracotta transition-colors duration-500 leading-none">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <ChevronDown
                          className={`md:hidden w-5 h-5 text-terracotta transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                          data-testid={`method-step-chevron-${i}`}
                        />
                      </div>
                      <div className="md:col-span-6">
                        <h3 className="font-serif text-2xl md:text-4xl text-ink tracking-tight mb-1">{s.title}</h3>
                        {/* Mobile: summary stays visible even when collapsed */}
                        <span className="md:hidden font-hand text-lg text-terracotta" data-testid={`method-step-summary-${i}`}>
                          {s.summary}
                        </span>
                      </div>
                      <p className="hidden md:block md:col-span-4 font-body text-ink-soft leading-relaxed">{s.body}</p>
                    </div>
                    <div
                      className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-[600px] opacity-100 pb-7" : "max-h-0 opacity-0"}`}
                      data-testid={`method-step-content-${i}`}
                    >
                      <p className="font-body text-ink-soft leading-relaxed">{s.body}</p>
                    </div>
                  </article>
                </StaggerItem>
              );
            })}
        </StaggerGroup>

        <Reveal delay={0.1} className="mt-10">
          <TextCTA href={l("/how-we-work")} testId="method-explore-cta">
            {t("landing.method_cta")}
          </TextCTA>
        </Reveal>
      </div>
    </section>
  );
};
